import * as _ from 'lodash';
import { Utils } from "../Utils/Utils";
import { DateUtils, DateType } from "../Utils/DateUtils";
import { StringUtils } from "../Utils/StringUtils";
import { ArgValidator} from '../Utils/ArgValidator';
import * as moment from 'moment';

export class DateTimePickerApi {
    private _id: string;
    private _idDate: string;
    private _idDateBtn: string;
    private _idTime: string;
    private _idTimeBtn: string;

    private _jqDate: JQuery;
    private _jqTime: JQuery;

    private _datePicker: JQuery;
    private _timePicker: JQuery;

    protected _dateFormat: string;
    protected _timeFormat: string;

    private _hasTime: boolean;

    constructor(id: string) {
        ArgValidator.ensureStringIsNotNullOrEmpty(id, 'id');
        this._id = id;
        this._idDate = `${id}-date-container`;
        this._idDateBtn = `${id}-date-button`;
        this._idTime = `${id}-time`;
        this._idTimeBtn = `${id}-time-button`;

        this._jqDate = Utils.getJQueryById(this._idDate);
        this._jqTime = Utils.getJQueryById(this._idTime);

        this._dateFormat = this._jqDate.data('format');
        this._timeFormat = this._jqTime.data('format');

        this._hasTime = !Utils.isNullOrUndefined(this._jqTime) && this._jqTime.length === 1;
    }

    public get hasTime(): boolean {
        return this._hasTime;
    }

    protected getInitOptions(component: 'date' | 'time'): Object {
        return null;
    }

    protected getComponent(component: 'date' | 'time', doInit: boolean = false): JQuery {
        var result = component === 'date' ? this._datePicker : this._timePicker;
        if (!result) {
            var opt = doInit ? this.getInitOptions(component) : undefined;
            if (component === 'date') {
                this._datePicker = this._jqDate.datepicker(opt);
                result = this._datePicker;
            } else {
                this._timePicker = this._jqTime.timepicker(opt);
                result = this._timePicker;
            }
        }

        return result;
    }

    private callApi(component: 'date' | 'time', options: any, value?: any): any {
        var cmp = this.getComponent(component);
        if (component === 'date') {
            return this._jqDate.datepicker(options, value);
        }

        return this._jqTime.timepicker(options, value);
    }

    public getUTCDate(): moment.Moment {
        return this.getDate(true);
    }

    /*public getLocalDate(): moment.Moment {
        return this.getDate(false);
    }*/

    private getDate(utc: boolean): moment.Moment {
        var result: moment.Moment = null;
        var dateValue = this.callApi('date', utc ? 'getUTCDate' : 'getDate');
        var date = dateValue ? (utc ? DateUtils.asUtcMoment(dateValue) : DateUtils.toLocal(dateValue)) : null;
        if (!Utils.isNullOrUndefined(date)) {
            result = date;

            if (this.hasTime) {
                this.getComponent('time');
                var timeValue = this._timePicker.data("timepicker").getTime();

                if (!StringUtils.isNullOrEmpty(timeValue)) {
                    var strDate = date.format(DateUtils.Formats.Date.Format);
                    result = moment.utc(strDate + ' ' + timeValue, DateUtils.Formats.DateTime.Format24Seconds);
                }
            }
        }

        return result;
    }

    public getUnixTimestamp(): number {
        var date = this.getUTCDate();
        return date ? DateUtils.asUnixTimestamp(date) : 0;
    }

    public clearDates(): void {
        this.callApi('date', 'clearDates');
        this.callApi('time', 'setTime', null);
    }

    public setUTCDate(date: DateType): void {
        var utcDate = DateUtils.asUtcMoment(date);
        this.callApi('date', 'setUTCDate', utcDate.toDate());

        var time = utcDate.format(this._timeFormat);
        this.callApi('time', 'setTime', time);
    }
}

export class DateTimePicker extends DateTimePickerApi {
    private _options: DateTimePickerOptions;

    constructor(id: string, options: DateTimePickerOptions) {
        super(id);
        this._options = options;
        this.getComponent('date', true);
        this.getComponent('time', true);
    }

    protected getInitOptions(component: 'date' | 'time'): Object {
        if (component === 'date') {
            return this.getDateOptions();
        } else if (component === 'time') {
            return this.getTimeOptions();
        } 

        throw new Error(`Unknown component type '${component}'`);
    }

    private getDateOptions(): Object {
        var o = this._options.date;
        return {
            autoclose: o.autoclose,
            todayBtn: o.todayBtn,
            format: o.format,
            todayHighlight: true
        };
    }

    private getTimeOptions(): Object {
        var o = this._options.time;
        return {
            template: 'dropdown',
            maxHours: 24,
            snapToStep: o.snapToStep,
            minuteStep: o.minuteStep,
            showSeconds: o.showSeconds,
            secondStep: o.secondStep,
            showMeridian: o.showMeridian,
            showInputs: true,
            disableFocus: false,
            disableMousewheel: true,
            modalBackdrop: false,
            //appendWidgetTo: 'body',
            explicitMode: false,
            owerflowUpdateParent: false,
            defaultTime: this._options.timeValue
        };
    }
}

export interface DateTimePickerOptions {
    date: {
        autoclose: boolean;
        todayBtn: string;
        format: string;
    };

    dateValue: string;

    time: {
        /**
         * @snapToStep - If true, setting the time will snap it to the closest "step",
         * either minute or second, depending on which unit is currently highlighted.
         * If the number would otherwise snap to 60 higher, the unit "overflows" to 0.
         */
        snapToStep: boolean;

        /**
         * @minuteStep - Specify a step for the minute field.
         */
        minuteStep: number;

        /**
         * @showSeconds - Show the seconds field.
         */
        showSeconds: boolean;

        /**
         * @secondStep - Specify a step for the second field.
         */
        secondStep: number;

        /**
         * @defaultTime - Set to the current time.
         */
        //defaultTime: string;

        /**
         * @showMeridian - true - 12hr mode, false - 24hr mode
         */
        showMeridian: boolean;
    };

    timeValue: string;

    mode: 'DateTime' | 'Date';
}


/** WEBPACK FOOTER **
 ** ./Typescript/Widgets/DateTimePicker.ts
 **/